import axios from 'axios';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://uk48cccg8sc8o8sws4okg4ok.147.93.95.174.sslip.io';
const API_VERSION = 'v1';
const DEBUG_MODE = import.meta.env.DEV;

// Enhanced logging function
const apiLog = (message, data = {}, level = 'info') => {
  if (DEBUG_MODE) {
    const timestamp = new Date().toISOString();
    const logMessage = `[ApiClient ${timestamp}] ${message}`;

    switch (level) {
      case 'error':
        console.error(logMessage, data);
        break;
      case 'warn':
        console.warn(logMessage, data);
        break;
      default:
        console.log(logMessage, data);
    }
  }
};

// Log API configuration on startup
apiLog('API Client initialized', {
  baseURL: `${API_BASE_URL}/api/${API_VERSION}`,
  timeout: 30000,
  debugMode: DEBUG_MODE
});

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/${API_VERSION}`,
  timeout: 30000, // 30 seconds timeout for AI operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add authentication token and log requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

    apiLog('Outgoing request', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : null,
      hasData: !!config.data
    });

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      apiLog('Authorization header added', { tokenLength: token.length });
    } else {
      apiLog('No auth token found for request', {}, 'warn');
    }

    return config;
  },
  (error) => {
    apiLog('Request interceptor error', { error: error.message }, 'error');
    return Promise.reject(error);
  }
);

// Response interceptor for global error handling and logging
apiClient.interceptors.response.use(
  (response) => {
    apiLog('Response received', {
      status: response.status,
      statusText: response.statusText,
      url: response.config?.url,
      method: response.config?.method?.toUpperCase(),
      hasData: !!response.data,
      dataType: typeof response.data
    });

    return response;
  },
  (error) => {
    const errorDetails = {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      responseData: error.response?.data
    };

    apiLog('Response error', errorDetails, 'error');

    // Handle common error scenarios
    if (error.response?.status === 401) {
      apiLog('Unauthorized error - clearing auth data', {}, 'warn');
      // Unauthorized - clear auth token and redirect appropriately
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      localStorage.removeItem('user');

      // Check current path to determine appropriate redirect
      const currentPath = window.location.pathname;

      // If we're on a public login or registration page, don't redirect
      if (currentPath.startsWith('/public/login') ||
        currentPath.startsWith('/public/register') ||
        currentPath.startsWith('/public/forgot-password')) {
        apiLog('On public auth page, not redirecting', { currentPath });
      }
      // If we're on any other page, redirect to appropriate login
      else if (currentPath.startsWith('/public')) {
        apiLog('Redirecting to public login page');
        window.location.href = '/public/login';
      } else {
        apiLog('Redirecting to admin login page');
        window.location.href = '/login';
      }
    } else if (error.response?.status === 403) {
      // Forbidden - insufficient permissions
      apiLog('Access denied: Insufficient permissions', errorDetails, 'error');
    } else if (error.response?.status >= 500) {
      // Server errors
      apiLog('Server error detected', {
        error: error.response?.data?.error || error.message,
        status: error.response?.status
      }, 'error');
    } else if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
      apiLog('Network connection error - backend may be down', {
        baseURL: API_BASE_URL,
        message: error.message
      }, 'error');
    }

    return Promise.reject(error);
  }
);

// Helper function to handle API responses
export const handleApiResponse = (response) => {
  if (response.data?.success) {
    return response.data.data;
  } else {
    throw new Error(response.data?.error || 'API request failed');
  }
};

// Helper function to handle API errors
export const handleApiError = (error) => {
  // Handle different error response formats
  if (error.response?.data?.error) {
    // Check if error is an object with a message property
    if (typeof error.response.data.error === 'object' && error.response.data.error.message) {
      throw new Error(error.response.data.error.message);
    }
    // If it's a string, use it directly
    else if (typeof error.response.data.error === 'string') {
      throw new Error(error.response.data.error);
    }
    // Fallback
    else {
      throw new Error('API request failed');
    }
  } else if (error.message) {
    throw new Error(error.message);
  } else {
    throw new Error('An unexpected error occurred');
  }
};

export default apiClient;